import { Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  Component,
  ElementRef,
  HostBinding,
  EventEmitter,
  Output,
  Input,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';

import { IEstimatedSavings, IGroupData, IUpsellBannerGroupDetail, IUpsellConfigData } from '@app/admin/fleet-card/shared/nudge-upsells/interfaces';
import { FLEET_CARDS_UPSELL_TYPE, FleetCardsUpsellService } from '@app/admin/fleet-card/shared/nudge-upsells/upsell-service';
import { LocalStorage } from '@app/services/local-storage';
import { Session } from '@services/session';
import { PendoAnalytics } from '@services/pendo-analytics';

import { BTN_TEXT_KEYS, PROMO_CONTENT_CONFIG } from './sticky-promo-banner.constants';

const MAX_DISMISS_COUNT = 2;

@Component({
  selector: 'sticky-promo-banner',
  templateUrl: './sticky-promo-banner.component.html',
  styleUrls: ['./sticky-promo-banner.component.scss'],
})

export class StickPromoBannerComponent implements OnInit, OnDestroy {
  @Input() public messageType: string;
  @Output() public onBannerClose = new EventEmitter();
  @Output() public onBannerOpen = new EventEmitter();
  @HostBinding('class.hide') public isBannerClosed: boolean = true;
  public config: IUpsellBannerGroupDetail;
  public potentialSavings: string;
  public isShownUpsellTracked: boolean = false;
  public header: string;
  public body: string;
  public primaryBtnText: string;
  public secondaryBtnText: string;
  public upsellId: string;
  public upsellGroupData: IGroupData;
  private routerSubscription: Subscription;

  constructor(
    public el: ElementRef,
    private router: Router,
    public session: Session,
    private localStorage: LocalStorage,
    private fleetCardUpsellService: FleetCardsUpsellService,
    private currencyPipe: CurrencyPipe,
    private pendo: PendoAnalytics,
  ) { }

  public ngOnInit(): void {
    this.doCardsUpsellBannerChecks(this.router.url);

    this.routerSubscription = this.router.events.subscribe(e => {
      if (e instanceof NavigationEnd) {
        const canBannerAppear = this.shouldCardBannerAppear(this.upsellGroupData, e.urlAfterRedirects);

        if (canBannerAppear) {
          this.setPromoContent(this.config, this.potentialSavings);
          this.trackPendoCardUpsellView(this.upsellGroupData);
          this.showBanner();
        } else {
          this.hideBanner();
        }
      }
    });
  }

  private shouldCardBannerAppear(upsellGroupData: IGroupData, url: string): boolean {
    if (!upsellGroupData?.group) { return false; }

    const config: IUpsellBannerGroupDetail = PROMO_CONTENT_CONFIG[upsellGroupData?.group];
    if (!config) { return false; } else { this.config = config; }

    const potentialSavings: string = this.getAnnualizedTotalSavings(upsellGroupData);
    if (!potentialSavings) { return false; } else { this.potentialSavings = potentialSavings; }

    const isNextDisplayDateReached: boolean = this.isNextDisplayDateReached();
    if (!isNextDisplayDateReached) { return false; }

    const canDismiss: boolean = this.canDismiss();
    if (!canDismiss) { return false; }

    const isCardUpsellPage: boolean = this.isCardUpsellPage(url);
    if (isCardUpsellPage) { return false; }

    const isContactSalesClicked: boolean = this.fleetCardUpsellService.getContactSalesClickStatus();
    if (isContactSalesClicked) { return false; }

    return true;
  }

  private doCardsUpsellBannerChecks(url: string): void {
    this.fleetCardUpsellService.canShowCardUpsellStickyBanner().subscribe((canShow: boolean) => {
      if (canShow) {
        this.fleetCardUpsellService.getEstimatedSavings().pipe(catchError(() => of(null)))
          .subscribe((estimatedSavings: IEstimatedSavings | null) => {
            if (!estimatedSavings) { this.hideBanner(); return; }

            this.upsellGroupData = this.fleetCardUpsellService.determineGroupAndVariant(estimatedSavings);
            if (!this.upsellGroupData?.group) { this.hideBanner(); return; }

            const canBannerAppear = this.shouldCardBannerAppear(this.upsellGroupData, url);
            if (!canBannerAppear) { this.hideBanner(); return; }
            this.setPromoContent(this.config, this.potentialSavings);
            this.trackPendoCardUpsellView(this.upsellGroupData);
            this.showBanner();
          });
      } else { this.hideBanner(); return; }
    });
  }

  public ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }

  private getDateAsString(date: Date = new Date()): string {
    return date.toISOString().split('T')[0];
  }

  private dateAfterDays(days: number): string {
    const newDate = new Date();
    newDate.setDate(newDate.getDate() + days);
    return newDate.toISOString().split('T')[0];
  }

  private getAnnualizedTotalSavings(data: IGroupData): string {
    return this.currencyPipe.transform(data?.estimatedSavings?.annualizedTotalSavings, 'USD', 'symbol', '1.0-0')?.toString();
  }

  private setPromoContent(config: IUpsellBannerGroupDetail, estimatedSavingsAmount: string): void {
    this.header = typeof config.header === 'function' ? config?.header(estimatedSavingsAmount) : config?.header;
    this.body = config?.body;
    this.primaryBtnText = config?.primaryBtnText;
    this.secondaryBtnText = config?.secondaryBtnText;
    this.upsellId = config.upsellId;
  }

  public handleCTAClick(e: Event): void {
    const target = e.target as HTMLElement;
    switch (target.innerText) {
    case BTN_TEXT_KEYS.applyNow:
      this.fleetCardUpsellService.handleApplyNowClick(FLEET_CARDS_UPSELL_TYPE.GLOBAL_BANNER_UPSELL, this.upsellGroupData);
      this.closeBanner();
      break;
    case BTN_TEXT_KEYS.imInterested:
      this.fleetCardUpsellService.handleContactSalesClick(FLEET_CARDS_UPSELL_TYPE.GLOBAL_BANNER_UPSELL, this.upsellGroupData);
      this.closeBanner();
      break;
    case BTN_TEXT_KEYS.learnMore:
      this.fleetCardUpsellService.handleLearnMoreClick(FLEET_CARDS_UPSELL_TYPE.GLOBAL_BANNER_UPSELL, this.upsellGroupData);
      this.closeBanner();
      break;
    default:
      this.hideBanner();
    }
  }

  private setNextDisplayDate(date: string) {
    this.localStorage.setUpsellStickyPromoBannerNextDisplayDate(date);
  }

  private getNextDisplayDate(): string {
    return this.localStorage.getUpsellStickyPromoBannerNextDisplayDate();
  }

  private setDismissCount(count: number) {
    this.localStorage.setUpsellStickyPromoBannerDismissCount(count);
  }

  private getDismissCount(): number {
    return this.localStorage.getUpsellStickyPromoBannerDismissCount() || 0;
  }

  private isCardUpsellPage(url: string): boolean {
    return this.fleetCardUpsellService.cardUpsellPages(url);
  }

  private isNextDisplayDateReached(): boolean {
    return new Date(this.getNextDisplayDate()) <= new Date(this.getDateAsString());
  }

  private canDismiss(): boolean {
    return this.getDismissCount() < MAX_DISMISS_COUNT;
  }

  private trackPendoCardUpsellView(groupData: IGroupData): void {
    if (!this.isShownUpsellTracked) {
      const upsellId = FLEET_CARDS_UPSELL_TYPE.GLOBAL_BANNER_UPSELL;
      const additionalAttributes = { group: groupData.group };
      this.pendo.trackEvent(this.fleetCardUpsellService.generateEventTag(upsellId, 'View'), additionalAttributes);

      this.isShownUpsellTracked = true;
    }
  }

  public showBanner(): void {
    this.isBannerClosed = false;
    this.onBannerClose.emit();
  }

  public hideBanner(): void {
    this.isBannerClosed = true;
    this.onBannerClose.emit();
  }

  public closeBanner(): void {
    this.setDismissCount(this.getDismissCount() + 1);

    const nextDisplayDate = (this.getDismissCount() >= MAX_DISMISS_COUNT) ? this.dateAfterDays(365) : this.dateAfterDays(7);
    this.setNextDisplayDate(nextDisplayDate);

    this.isBannerClosed = true;
    this.onBannerClose.emit();
  }
}
